/**
 * Country-specific mobile number length validation.
 *
 * The allowed lengths come from the libphonenumber-js mobile metadata (Google libphonenumber
 * data), so no per-country digit counts are hardcoded. A length is counted on the national
 * number: no country code and no trunk 0 (Iraq 0770 123 4567 is entered as 770 123 4567, 10 digits).
 *
 * The same file exists in tiryaq-doctor-pharmacy-app, tiryaq-lab-app, tiryaq-imaging-app,
 * tiryaq-nurse-app and tiryaq-physiotherapy-app (src/utils/phoneValidation.js). The admin panel
 * has a TypeScript copy (src/utils/phoneValidation.ts). Keep them in step.
 */
import { Metadata } from "libphonenumber-js/core";
import mobileMetadata from "libphonenumber-js/metadata.mobile.json";
import { countryDisplayName } from "../constants/countries";

/** E.164 limit, used only for a country the metadata does not know. */
const E164_MAX_DIGITS = 15;

const lengthsByCountry = new Map();

/**
 * Valid national mobile number lengths for an ISO 3166-1 alpha-2 code ("iq" or "IQ"), ascending:
 * [10] for Iraq, [9] for Saudi Arabia, [10, 11] for Germany. null when the country is unknown.
 */
export const getMobileLengths = (iso) => {
  const country = String(iso || "").toUpperCase();
  if (lengthsByCountry.has(country)) return lengthsByCountry.get(country);
  let lengths = null;
  try {
    const metadata = new Metadata(mobileMetadata);
    metadata.selectNumberingPlan(country);
    const plan = metadata.numberingPlan;
    // Lengths of the MOBILE number type; the plan-wide list (every number type) is the fallback.
    const mobile = typeof plan.type === "function" ? plan.type("MOBILE") : undefined;
    const found = (mobile && mobile.possibleLengths()) || plan.possibleLengths();
    if (Array.isArray(found) && found.length > 0) {
      lengths = [...found].sort((a, b) => a - b);
    }
  } catch {
    lengths = null; // Country not in the metadata.
  }
  lengthsByCountry.set(country, lengths);
  return lengths;
};

/** The most digits a mobile number may have for the country (the input stops there). */
export const getMobileMaxLength = (iso) => {
  const lengths = getMobileLengths(iso);
  return lengths ? lengths[lengths.length - 1] : E164_MAX_DIGITS;
};

/** Arabic-Indic (٠-٩) and Persian (۰-۹) digits as ASCII digits, so Arabic keyboards work. */
const toAsciiDigits = (value) =>
  String(value ?? "")
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

/**
 * Typed or pasted text as a national mobile number for the country: ASCII digits only,
 * no leading 0 (the country code replaces the trunk 0) and never longer than the country allows.
 */
export const limitMobileDigits = (value, iso) =>
  toAsciiDigits(value)
    .replace(/\D/g, "")
    .replace(/^0+/, "")
    .slice(0, getMobileMaxLength(iso));

/** Allowed lengths as message text: "10", "9-10" for a range, "7/9" for separate lengths. */
export const formatMobileLengths = (lengths) => {
  if (!lengths || lengths.length === 0) return "";
  const min = lengths[0];
  const max = lengths[lengths.length - 1];
  if (min === max) return String(min);
  return max - min + 1 === lengths.length ? `${min}-${max}` : lengths.join("/");
};

/**
 * Length problem of a national mobile number for the country, or null when the length is valid.
 * An empty number returns null: each form keeps its own "required" check.
 * @returns {{ tooLong: boolean, digits: string } | null} `digits` is the allowed length text.
 */
export const getMobileLengthIssue = (national, iso) => {
  const length = String(national ?? "").replace(/\D/g, "").length;
  if (length === 0) return null;
  const lengths = getMobileLengths(iso);
  if (!lengths || lengths.includes(length)) return null;
  return {
    tooLong: length > lengths[lengths.length - 1],
    digits: formatMobileLengths(lengths),
  };
};

/** Country name in the UI language ("العراق" in Arabic), or `fallback` when the browser can't say. */
export const getCountryName = (iso, language, fallback = "") => {
  try {
    const names = new Intl.DisplayNames([language || "en"], { type: "region" });
    return names.of(String(iso || "").toUpperCase()) || fallback;
  } catch {
    return fallback || String(iso || "").toUpperCase();
  }
};

/**
 * The translated length message, e.g. "Enter a valid Iraq mobile number (digits: 10)".
 * `country` is a picker entry ({ iso, name }); a `countries.<iso>` translation wins when present,
 * then the name the country picker shows (constants/countries.js).
 */
export const mobileLengthErrorText = (t, language, country, issue) =>
  t("validation.mobileLength", {
    country: t(`countries.${country?.iso}`, {
      defaultValue: country?.name
        ? countryDisplayName(country, language)
        : getCountryName(country?.iso, language, country?.iso),
    }),
    digits: issue.digits,
  });
